import React from "react";
import { Box, Grid, Button, Typography, Chip } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import moment from "moment-timezone";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import Loader from "../components/Loader";
import { tokens } from "../theme";

/* Local dashboard API (Spring service). Override with
   REACT_APP_API_BASE_URL when it ships to stage/prod. */
const OVERVIEW_API_BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5050/";

/* ---------- helpers ---------- */
const mono = '"IBM Plex Mono", monospace';
const disp = '"Space Grotesk", sans-serif';

const fmtMoney = (n) => {
  if (n == null) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}k`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const fmtCount = (n) => {
  if (n == null) return "—";
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return `${n}`;
};

const MONEY_TICKER_LABELS = ["deposits today", "GGR today", "net flow", "withdrawals", "house P&L"];

const alertColor = (level) => {
  const map = {
    danger: tokens.danger,
    red: tokens.danger,
    warn: tokens.amber,
    warning: tokens.amber,
    amber: tokens.amber,
    ok: tokens.secondary,
    success: tokens.secondary,
    green: tokens.secondary,
    info: tokens.primary,
    primary: tokens.primary,
    pink: tokens.pink,
  };
  return map[String(level || "").toLowerCase()] || tokens.textDim;
};

// Scrolling LIVE ticker
function Ticker({ items }) {
  const doubled = [...items, ...items];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: 38,
        mb: 1.75,
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
        background: tokens.bg2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: "none",
          px: 1.6,
          height: "100%",
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          bgcolor: tokens.selected,
          color: tokens.primary,
          fontSize: 11,
          fontWeight: 700,
          borderRight: `1px solid ${tokens.border}`,
        }}
      >
        <BoltRoundedIcon sx={{ fontSize: 15 }} /> LIVE
      </Box>
      <Box sx={{ overflow: "hidden", flex: 1 }}>
        <Box
          className="ss-ticker-track"
          sx={{
            display: "flex",
            gap: 4.2,
            whiteSpace: "nowrap",
            pl: 2.5,
            width: "max-content",
            animation: "ticker-scroll 40s linear infinite",
            "& span": { fontSize: 12, color: tokens.textDim, fontFamily: mono },
            "& b": { color: tokens.text },
          }}
        >
          {doubled.map((t, i) => (
            <span key={i}>
              <b>{t.value}</b> {t.label}
            </span>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// SVG ring gauge
function Ring({ pct, color, size = 118 }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const off = circ * (1 - clamped / 100);
  return (
    <svg width={size} height={size} viewBox="0 0 110 110">
      <circle cx="55" cy="55" r={r} fill="none" stroke={tokens.raised} strokeWidth="9" />
      <circle
        cx="55"
        cy="55"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={off}
        transform="rotate(-90 55 55)"
      />
      <text x="55" y="52" textAnchor="middle" fill={tokens.text} fontSize="22" fontFamily={disp} fontWeight="600">
        {pct ?? 0}%
      </text>
      <text x="55" y="69" textAnchor="middle" fill={tokens.textDim} fontSize="10" fontFamily={mono}>
        of target
      </text>
    </svg>
  );
}

// Compact KPI tile (SpinSaga miniK)
function MiniKpi({ label, value, delta, up, color }) {
  return (
    <Box
      sx={{
        background: tokens.cardBg,
        border: `1px solid ${tokens.border}`,
        borderRadius: "12px",
        p: "13px 15px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {color && <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", bgcolor: color }} />}
      <Typography sx={{ color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: disp, fontSize: 25, fontWeight: 600, mt: 0.7, letterSpacing: "-0.02em" }}>
        {value}
      </Typography>
      {delta && (
        <Typography
          sx={{
            fontFamily: mono,
            fontSize: 11.5,
            fontWeight: 600,
            mt: 0.4,
            color: up ? tokens.secondary : tokens.danger,
            display: "flex",
            alignItems: "center",
            gap: 0.3,
          }}
        >
          {up ? <ArrowUpwardRoundedIcon sx={{ fontSize: 13 }} /> : <ArrowDownwardRoundedIcon sx={{ fontSize: 13 }} />}
          {delta}
        </Typography>
      )}
    </Box>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box sx={{ bgcolor: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 2, p: 1.2 }}>
      {label && <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>}
      {payload.map((p) => (
        <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color || p.stroke }} />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize", fontFamily: mono }}>
            {p.dataKey}: {fmtMoney(p.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// Bold the "Category:" prefix of an alert line, like the design mock.
function AlertText({ text }) {
  const idx = String(text || "").indexOf(":");
  if (idx === -1) return <Typography sx={{ fontSize: 12.5, flex: 1 }}>{text}</Typography>;
  return (
    <Typography sx={{ fontSize: 12.5, flex: 1 }}>
      <b>{text.slice(0, idx + 1)}</b>
      {text.slice(idx + 1)}
    </Typography>
  );
}

export default function Dashboard() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    const fetchOverview = async () => {
      try {
        const sessionId = sessionStorage.getItem("session")?.replace('"', "").replace('"', "");
        const res = await fetch(`${OVERVIEW_API_BASE}dashboard/overview`, {
          headers: {
            Accept: "application/json",
            ...(sessionId ? { SessonId: sessionId } : {}),
          },
        });
        const body = await res.json();
        if (body?.status === "success") {
          setData(body.data);
        }
      } catch (err) {
        console.error("overview fetch failed", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOverview();
  }, []);

  const rt = data?.revenueTarget || {};
  const kpis = data?.kpis || {};

  const tickerItems = (data?.ticker || []).map((t) => ({
    label: t.label,
    value:
      t.label === "of target"
        ? `${t.value}%`
        : MONEY_TICKER_LABELS.includes(t.label)
        ? fmtMoney(t.value)
        : fmtCount(t.value),
  }));

  const deltaText = (k) =>
    k?.deltaPct != null ? `${Math.abs(k.deltaPct)}% vs yest` : null;

  const kpiTiles = [
    { label: "Deposits · Today", value: fmtMoney(kpis.depositsToday?.value), delta: deltaText(kpis.depositsToday), up: (kpis.depositsToday?.deltaPct ?? 0) >= 0, color: tokens.secondary },
    { label: "FTDs · Today", value: fmtCount(kpis.ftdsToday?.value), delta: deltaText(kpis.ftdsToday), up: (kpis.ftdsToday?.deltaPct ?? 0) >= 0, color: tokens.accent },
    { label: "Active Players", value: kpis.activePlayers?.value?.toLocaleString() ?? "—", delta: deltaText(kpis.activePlayers), up: (kpis.activePlayers?.deltaPct ?? 0) >= 0, color: tokens.blue },
    { label: "SC Liability", value: fmtMoney(kpis.scLiability?.value), delta: deltaText(kpis.scLiability), up: false, color: tokens.secondary },
    { label: "KYC Backlog", value: kpis.kycBacklog?.value?.toLocaleString() ?? "—", delta: kpis.kycBacklog?.value > 0 ? "blocks withdrawals" : null, up: false, color: tokens.amber },
    { label: "Birthdays Tmrw", value: fmtCount(kpis.birthdaysTomorrow?.value), delta: kpis.birthdaysTomorrow?.value > 0 ? "outreach" : null, up: true, color: tokens.pink },
  ];

  const revenueSeries = (data?.revenueSeries || []).map((d) => ({
    ...d,
    day: moment(d.day).format("MMM D"),
  }));

  const gameMix = data?.gameMix || [];
  const topGames = (data?.topGames || []).slice(0, 5);
  const transactions = data?.transactions || [];
  const alerts = data?.alerts || [];

  const refreshedAt = data?.generatedAt
    ? moment(data.generatedAt).format("HH:mm:ss")
    : "…";

  return (
    <Box>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Command Center"
        crumb="Overview"
        subtitle={`SpinSaga · real-time pulse · refreshed ${refreshedAt}`}
        actions={
          <>
            <Button variant="outlined" color="inherit" size="small" startIcon={<FileDownloadRoundedIcon />}>
              Export
            </Button>
            <Button variant="contained" size="small" startIcon={<DescriptionRoundedIcon />}>
              Daily brief
            </Button>
          </>
        }
      />

      {/* LIVE ticker */}
      {tickerItems.length > 0 && <Ticker items={tickerItems} />}

      {/* Ring + mini KPIs */}
      <Grid container spacing={1.75} alignItems="stretch">
        <Grid item xs={12} md={4} lg={3}>
          <SectionCard title="Daily revenue target" subtitle={`Pace toward ${fmtMoney(rt.target)}`} sx={{ height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Ring pct={rt.pct} color={tokens.accent} />
              <Box>
                <Typography variant="caption" color="text.secondary">Deposits today</Typography>
                <Typography sx={{ fontFamily: disp, fontSize: 24, fontWeight: 600 }}>{fmtMoney(rt.depositsToday)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>Projected EOD</Typography>
                <Typography sx={{ fontFamily: disp, fontSize: 16, color: tokens.secondary }}>{fmtMoney(rt.projectedEod)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Net flow (after {fmtMoney(rt.withdrawalsToday)} withdrawals)
                </Typography>
                <Typography sx={{ fontFamily: disp, fontSize: 16 }}>{fmtMoney(rt.netFlow)}</Typography>
              </Box>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Grid container spacing={1.75}>
            {kpiTiles.map((k) => (
              <Grid item xs={6} md={4} key={k.label}>
                <MiniKpi {...k} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Revenue chart + Live alerts */}
      <Grid container spacing={1.75} sx={{ mt: 0.25 }}>
        <Grid item xs={12} lg={7}>
          <SectionCard title="Deposits vs Withdrawals" subtitle="7-day · GGR overlay">
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueSeries} margin={{ left: -18, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="dDep" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={tokens.secondary} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={tokens.secondary} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="dWit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={tokens.primary} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={tokens.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                  <XAxis dataKey="day" stroke={tokens.textDim} fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke={tokens.textDim} fontSize={12} tickLine={false} axisLine={false} />
                  <RTooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="deposits" stroke={tokens.secondary} strokeWidth={2.5} fill="url(#dDep)" />
                  <Area type="monotone" dataKey="withdrawals" stroke={tokens.primary} strokeWidth={2.5} fill="url(#dWit)" />
                  <Area type="monotone" dataKey="ggr" stroke={tokens.accent} strokeWidth={2.5} fill="none" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Live alerts"
            action={<Chip size="small" label={`${alerts.length} active`} sx={{ bgcolor: tokens.surface2, color: tokens.textDim, fontWeight: 600 }} />}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {alerts.length === 0 && !isLoading && (
                <Typography sx={{ fontSize: 12.5, color: tokens.textDim }}>No active alerts</Typography>
              )}
              {alerts.map((a, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 1.4,
                    alignItems: "flex-start",
                    py: 1.1,
                    borderBottom: i < alerts.length - 1 ? `1px solid ${tokens.border}` : "none",
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: alertColor(a.level), mt: 0.7, flex: "none" }} />
                  <AlertText text={a.text} />
                  <Typography sx={{ color: tokens.textDim2, fontSize: 10.5, fontFamily: mono, whiteSpace: "nowrap" }}>
                    {a.time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Top games + Game mix */}
      <Grid container spacing={1.75} sx={{ mt: 0.25 }}>
        <Grid item xs={12} lg={7}>
          <SectionCard title="Top games today" subtitle="By gross gaming revenue">
            <Box>
              {topGames.length === 0 && !isLoading && (
                <Typography sx={{ fontSize: 12.5, color: tokens.textDim }}>No game activity today</Typography>
              )}
              {topGames.map((g, i) => (
                <Box
                  key={g.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 1.1,
                    borderBottom: i < topGames.length - 1 ? `1px solid ${tokens.border}` : "none",
                  }}
                >
                  <Box>
                    <Typography component="div" sx={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center" }}>
                      {g.name}
                      {g.hot && (
                        <Chip size="small" label="hot" sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: "rgba(35,214,160,0.13)", color: tokens.secondary }} />
                      )}
                    </Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim2 }}>
                      {g.provider} · {g.category}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto", textAlign: "right" }}>
                    <Typography sx={{ fontFamily: disp, fontWeight: 600, color: g.ggr < 0 ? tokens.danger : tokens.secondary }}>
                      {fmtMoney(g.ggr)}
                    </Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: 10.5, color: tokens.textDim }}>
                      {fmtCount(g.bets24h)} bets
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SectionCard title="Game mix" subtitle="Share of GGR by vertical">
            <Box sx={{ height: 230 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={gameMix} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={3} stroke="none">
                    {gameMix.map((g) => (
                      <Cell key={g.name} fill={g.color} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                  <Legend verticalAlign="bottom" iconType="circle" formatter={(v) => <span style={{ color: tokens.textDim, fontSize: 12 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Recent transactions */}
      <Grid container spacing={1.75} sx={{ mt: 0.25 }}>
        <Grid item xs={12}>
          <SectionCard title="Recent transactions" action={<Button size="small" href="/withdrawals">View all</Button>}>
            <Box>
              {transactions.length === 0 && !isLoading && (
                <Typography sx={{ fontSize: 12.5, color: tokens.textDim }}>No recent transactions</Typography>
              )}
              {transactions.slice(0, 6).map((t, i) => (
                <Box
                  key={t.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1.1,
                    borderBottom: i < Math.min(transactions.length, 6) - 1 ? `1px solid ${tokens.border}` : "none",
                  }}
                >
                  <Typography sx={{ fontFamily: mono, fontSize: 12, color: tokens.textDim, width: 90 }}>{t.id}</Typography>
                  <Typography sx={{ fontSize: 13, flex: 1 }}>{t.player}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.type === "Deposit" ? tokens.secondary : tokens.primary, width: 110 }}>
                    {t.type}
                  </Typography>
                  <Typography sx={{ fontFamily: mono, fontSize: 12, width: 70 }}>{t.coin}</Typography>
                  <Typography sx={{ fontFamily: disp, fontWeight: 600, width: 110, textAlign: "right" }}>
                    ${Number(t.usd ?? 0).toLocaleString()}
                  </Typography>
                  <Chip
                    size="small"
                    label={t.status}
                    sx={{
                      width: 100,
                      bgcolor:
                        t.status === "Completed" ? "rgba(35,214,160,0.13)" :
                        t.status === "Failed" ? "rgba(242,92,102,0.13)" : "rgba(245,166,35,0.13)",
                      color:
                        t.status === "Completed" ? tokens.secondary :
                        t.status === "Failed" ? tokens.danger : tokens.amber,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              ))}
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
